import {
  Button,
  ButtonIconPosition,
  ButtonSize,
  ButtonVariant,
} from "@amzn/stencil-react-components/button";
import { FlyoutContent } from "@amzn/stencil-react-components/flyout";
import { Row } from "@amzn/stencil-react-components/layout";
import { H2 } from "@amzn/stencil-react-components/text";
import IconChevronLeftMedium from "@amzn/stencil-react-icons/icons/icon-chevron-left-medium";
import { useIntl } from "react-intl";

/** A leading "back" action rendered on the left of the panel header. */
export interface ChatPanelBackAction {
  /** i18n id for the button label, e.g. "Back to Aiva". */
  labelId: string;
  onClick: () => void;
  dataTestId: string;
}

/**
 * Shared chrome for the two chat surfaces — Aiva and the IT Support team.
 *
 * Both channels render through this one component so their headers cannot drift
 * apart. `FlyoutContent` supplies the 600px panel, the title as an H2, the close
 * button, and body padding that already steps 24px -> 16px -> 8px across
 * breakpoints and high zoom.
 *
 * `backAction` fills the header's leading slot with a labelled secondary button
 * (the design hides the bare back-arrow instance in favour of this), letting the
 * user hop between the two channels.
 */
export const ChatPanel = ({
  titleId,
  titleElementId,
  backAction,
  onClose,
  footer,
  children,
  dataTestId,
  fullBleedBody = false,
}: {
  titleId: string;
  /**
   * DOM id applied to the title heading. Set this when a parent dialog needs to
   * reference the title via `aria-labelledby`; Stencil puts its own id on a
   * wrapper that also contains the back button, so pointing at that would fold
   * the button's label into the dialog's accessible name.
   */
  titleElementId?: string;
  backAction?: ChatPanelBackAction;
  onClose?: () => void;
  footer?: React.ReactNode;
  children: React.ReactNode;
  dataTestId?: string;
  /**
   * Drops the panel body's padding. Used for embedded content that brings its
   * own chrome — the Connect chat widget draws edge to edge, so the standard
   * body inset would double up on its internal spacing.
   */
  fullBleedBody?: boolean;
}): JSX.Element => {
  const intl = useIntl();

  return (
    <FlyoutContent
      titleText={intl.formatMessage({ id: titleId })}
      renderTitle={({ titleText }) => (
        <Row gridGap="S300" alignItems="center">
          {backAction && (
            <Button
              variant={ButtonVariant.Secondary}
              size={ButtonSize.Small}
              icon={<IconChevronLeftMedium aria-hidden />}
              iconPosition={ButtonIconPosition.Leading}
              onClick={backAction.onClick}
              dataTestId={backAction.dataTestId}
            >
              {intl.formatMessage({ id: backAction.labelId })}
            </Button>
          )}
          <H2 id={titleElementId} fontSize="T300">
            {titleText}
          </H2>
        </Row>
      )}
      onCloseButtonClick={onClose ?? null}
      closeButtonAltText={intl.formatMessage({
        id: "arialabels-clickToCloseChat",
      })}
      buttons={footer ? [footer] : undefined}
      dataTestId={dataTestId}
      // Styling hook for the header height and body padding rules in
      // Chatbot.scss. `FlyoutContent` drops `className` but forwards any `data-*`
      // prop, which avoids wrapping the panel in an extra element.
      data-chat-panel={fullBleedBody ? "full-bleed" : "true"}
    >
      {children}
    </FlyoutContent>
  );
};
